<template>
    <div class="border border-border bg-white p-4 dark:bg-card">
        <!-- Metric cards + timeframe -->
        <div class="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-start">
            <div class="flex w-full flex-wrap gap-2">
                <!-- Usage (data) -->
                <button
                    type="button"
                    class="flex w-full flex-col gap-4 px-3 pb-4 pt-3 text-left shadow-sm transition-colors sm:w-[250px]"
                    :class="
                        selectedMetric === 'usage'
                            ? 'bg-new-topol-blue text-white'
                            : 'text-body-muted hover:bg-surface-card dark:hover:bg-muted'
                    "
                    @click="selectedMetric = 'usage'"
                >
                    <p class="text-xs">Usage</p>
                    <div class="flex flex-col gap-2">
                        <div class="flex items-end gap-1">
                            <p class="font-display text-[40px] font-medium leading-none">
                                {{ usageData.value }}
                            </p>
                            <p class="font-display text-[28px] font-medium leading-[1.05]">
                                {{ usageData.unit }}
                            </p>
                        </div>
                        <p
                            class="text-[10px]"
                            :class="selectedMetric === 'usage' ? 'text-white/80' : ''"
                        >
                            {{ timeframeLabel }}
                        </p>
                    </div>
                </button>

                <!-- Active users -->
                <button
                    type="button"
                    class="flex w-full flex-col gap-4 px-3 pb-4 pt-3 text-left shadow-sm transition-colors sm:w-[250px]"
                    :class="
                        selectedMetric === 'active-users'
                            ? 'bg-new-topol-blue text-white'
                            : 'text-body-muted hover:bg-surface-card dark:hover:bg-muted'
                    "
                    @click="selectedMetric = 'active-users'"
                >
                    <p class="text-xs">Active users</p>
                    <div class="flex flex-col gap-2">
                        <p class="font-display text-[40px] font-medium leading-none">
                            {{ totals.activeUsers }}
                        </p>
                        <p
                            class="text-[10px]"
                            :class="selectedMetric === 'active-users' ? 'text-white/80' : ''"
                        >
                            {{ timeframeLabel }}
                        </p>
                    </div>
                </button>
            </div>

            <!-- Timeframe -->
            <div class="flex shrink-0 flex-col items-end gap-2">
                <DropdownMenu :modal="false">
                    <DropdownMenuTrigger as-child>
                        <button
                            type="button"
                            class="flex h-10 shrink-0 items-center gap-1 border border-border bg-surface-card px-4 py-2 text-sm text-foreground transition-colors hover:bg-[#eceef1] dark:bg-muted"
                        >
                            {{ currentTimeframeLabel }}
                            <ChevronDown class="size-4 text-muted-foreground" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            v-for="option in timeframeOptions"
                            :key="option.key"
                            class="text-sm"
                            :class="{ 'font-medium': option.key === selectedTimeframe }"
                            @click="selectTimeframe(option.key)"
                        >
                            <Check
                                v-if="option.key === selectedTimeframe"
                                class="size-3.5"
                            />
                            <span v-else class="size-3.5" />
                            {{ option.label }}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                <!-- Custom range inputs -->
                <div
                    v-if="selectedTimeframe === 'custom'"
                    class="flex flex-wrap items-end gap-2 border border-border bg-surface-card p-3 dark:bg-muted"
                >
                    <label class="flex flex-col gap-1 text-xs text-muted-foreground">
                        From
                        <input
                            v-model="customFrom"
                            type="date"
                            :max="customTo || undefined"
                            class="h-9 border border-border bg-white px-2 text-sm text-foreground dark:bg-card"
                        />
                    </label>
                    <label class="flex flex-col gap-1 text-xs text-muted-foreground">
                        To
                        <input
                            v-model="customTo"
                            type="date"
                            :min="customFrom || undefined"
                            class="h-9 border border-border bg-white px-2 text-sm text-foreground dark:bg-card"
                        />
                    </label>
                    <Button
                        size="sm"
                        :disabled="!customFrom || !customTo"
                        @click="applyCustomRange"
                    >
                        Apply
                    </Button>
                </div>
            </div>
        </div>

        <!-- Chart -->
        <div class="relative mt-2">
            <div
                v-if="hasData"
                class="relative h-[400px] w-full bg-surface-card p-4 dark:bg-muted"
            >
                <canvas ref="chartCanvas"></canvas>
            </div>
            <div
                v-else
                class="flex h-[400px] w-full items-center justify-center bg-surface-card dark:bg-muted"
            >
                <p class="text-xs text-muted-foreground">
                    {{ emptyMessage }}
                </p>
            </div>

            <!-- Loading overlay while a new timeframe streams in -->
            <div
                v-if="reloading"
                class="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-card/60"
            >
                <Loader2 class="size-6 animate-spin text-muted-foreground" />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick, PropType } from 'vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import Chart from 'chart.js/auto';
import { ChevronDown, Check, Loader2 } from 'lucide-vue-next';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { formatBytes } from '@/utils/formatBytes';
import type IUsage from '@/types/Plugin/IUsage';
import type { IUsageTimeframeOption } from '@/types/Plugin/IUsage';

type MetricKey = 'usage' | 'active-users';

const props = defineProps({
    usage: {
        type: Object as PropType<IUsage | null>,
        default: null,
    },
    apiTokenId: {
        type: String,
        required: true,
    },
    timeframe: {
        type: String,
        default: 'this-month',
    },
    timeframeOptions: {
        type: Array as PropType<IUsageTimeframeOption[]>,
        default: () => [],
    },
    customRange: {
        type: Object as PropType<{ from: string | null; to: string | null }>,
        default: () => ({ from: null, to: null }),
    },
});

const selectedMetric = ref<MetricKey>('usage');
const selectedTimeframe = ref<string>(props.timeframe || 'this-month');
const customFrom = ref<string>(props.customRange?.from ?? '');
const customTo = ref<string>(props.customRange?.to ?? '');
const reloading = ref(false);

const series = computed(() => props.usage?.series ?? []);
const totals = computed(() => props.usage?.totals ?? { activeUsers: 0, dataUsage: 0 });
const timeframeLabel = computed(() => props.usage?.label ?? 'This month');

const currentTimeframeLabel = computed(
    () =>
        props.timeframeOptions.find((o) => o.key === selectedTimeframe.value)?.label ??
        timeframeLabel.value,
);

const emptyMessage = computed(() =>
    selectedMetric.value === 'usage'
        ? 'No data traffic for this period yet.'
        : 'No active users for this period yet.',
);

const splitBytes = (bytes: number): { value: string; unit: string } => {
    if (!bytes) return { value: '0', unit: 'B' };
    const k = 1024;
    const sizes = ['B', 'kB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.min(
        Math.floor(Math.log(bytes) / Math.log(k)),
        sizes.length - 1,
    );
    // Whole numbers lose too much at TB+ scale (2 TB vs 2.4 TB), so keep one decimal there.
    const decimals = i >= 4 ? 1 : 0;
    return {
        value: String(parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))),
        unit: sizes[i],
    };
};

const usageData = computed(() => splitBytes(totals.value.dataUsage));

const chartLabels = computed(() => series.value.map((s) => s.label));
const chartValues = computed(() =>
    series.value.map((s) =>
        selectedMetric.value === 'usage' ? s.bytes : s.activeUsers,
    ),
);
const hasData = computed(() => chartValues.value.some((v) => v > 0));

const formatValue = (value: number, decimals = 0): string =>
    selectedMetric.value === 'usage'
        ? formatBytes(value, decimals)
        : String(Math.round(value));

const reload = (params: Record<string, string>) => {
    router.get(route('plugin.api-tokens.usage', { apiToken: props.apiTokenId }), params, {
        only: ['usage', 'timeframe', 'customRange'],
        preserveState: true,
        preserveScroll: true,
        onStart: () => (reloading.value = true),
        onFinish: () => (reloading.value = false),
    });
};

const selectTimeframe = (key: string) => {
    selectedTimeframe.value = key;
    // Custom range waits for the user to pick both dates and press Apply.
    if (key === 'custom') return;
    reload({ timeframe: key });
};

const applyCustomRange = () => {
    if (!customFrom.value || !customTo.value) return;
    reload({ timeframe: 'custom', from: customFrom.value, to: customTo.value });
};

const chartCanvas = ref<HTMLCanvasElement>();
let chartInstance: Chart | null = null;

const renderChart = () => {
    if (!chartCanvas.value || !hasData.value) return;

    if (chartInstance) {
        chartInstance.destroy();
    }

    chartInstance = new Chart(chartCanvas.value, {
        type: 'bar',
        data: {
            labels: chartLabels.value,
            datasets: [
                {
                    data: chartValues.value,
                    backgroundColor: '#0048ff',
                    barPercentage: 1,
                    categoryPercentage: 0.6,
                },
            ],
        },
        options: {
            maintainAspectRatio: false,
            interaction: { intersect: true, mode: 'index' },
            scales: {
                y: {
                    grid: { color: '#e6e8eb' },
                    ticks: {
                        callback: (value) =>
                            value !== 0
                                ? formatValue(parseInt(value.toString()), 0)
                                : undefined,
                        maxTicksLimit: 5,
                        color: '#6B7280',
                        font: { size: 10 },
                        precision: 0,
                    },
                },
                x: {
                    grid: { display: false },
                    ticks: { color: '#6B7280', font: { size: 10 } },
                },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: '#252F3F',
                    caretSize: 0,
                    cornerRadius: 6,
                    padding: 6,
                    bodyFont: { size: 12 },
                    callbacks: {
                        label: (ctx) =>
                            formatValue(parseInt(ctx.raw?.toString() || '0'), 2),
                        title: () => '',
                    },
                },
            },
        },
    });
};

watch([selectedMetric, () => props.usage], async () => {
    await nextTick();
    renderChart();
});

onMounted(() => {
    if (hasData.value) {
        renderChart();
    }
});

onBeforeUnmount(() => {
    if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
    }
});
</script>
